// src/index.js
import React from 'react';
import { createRoot } from 'react-dom/client';
import { CssBaseline, GlobalStyles } from '@mui/material';
import ShutdownApp from './ShutdownApp';
import './reset.css';
import { installEnvTitlePrefix } from './utils/envTitlePrefix';
import faviconUrl from './assets/favicon.ico';

// Shutdown branch: do not import App.js / bootstrap / analytics — keeps webpack lean.

// ─────────────────────────────────────────────────────────────────────────────
// Eliminate benign "ResizeObserver loop" warnings.
//
// Browsers emit two harmless warnings:
//   • "ResizeObserver loop limit exceeded"
//   • "ResizeObserver loop completed with undelivered notifications."
// when an observer callback dirties layout the browser can't deliver in the
// same frame. Tons of dependencies (MUI internals, ApexCharts, virtualized
// lists, our own tour overlay) legitimately trigger this during fast resizes.
// CRA / webpack-dev-server's red error overlay treats it as a fatal runtime
// error, so we silence it at multiple layers:
//
// 1. PATCH `ResizeObserver` at the source so every callback in the page is
//    deferred to the next frame via requestAnimationFrame. The browser
//    therefore never sees an "incomplete" delivery loop and stops emitting
//    the warning entirely. This is the most effective layer — without it,
//    only swallowing the resulting error message is a cat-and-mouse game.
//
// 2. Swallow any straggler ErrorEvent / PromiseRejectionEvent / console.error
//    that still mentions a "ResizeObserver loop" so the dev overlay never
//    surfaces it. All non-matching errors propagate normally.
(function installResizeObserverHardening() {
  if (typeof window === 'undefined') return;

  // ── Layer 1: defer every ResizeObserver callback ──────────────────────────
  if (typeof window.ResizeObserver === 'function' && !window.__roPatched) {
    const Original = window.ResizeObserver;
    const raf =
      typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (cb) => window.setTimeout(cb, 16);
    class DeferredResizeObserver extends Original {
      constructor(callback) {
        super((entries, observer) => {
          // Defer; if the deferred callback throws, swallow noise but rethrow
          // anything genuinely unexpected so real bugs still surface.
          raf(() => {
            try {
              callback(entries, observer);
            } catch (err) {
              const msg = (err && err.message) || '';
              if (/resizeobserver\s+loop/i.test(msg)) return;
              throw err;
            }
          });
        });
      }
    }
    try {
      window.ResizeObserver = DeferredResizeObserver;
      window.__roPatched = true;
    } catch {
      // Some environments freeze the constructor; the swallow layer below
      // covers us anyway.
    }
  }

  // ── Layer 2: swallow any RO loop messages that still surface ──────────────
  const resizeObsNoise = (s) =>
    typeof s === 'string' && /resizeobserver\s+loop/i.test(s.trim());
  const handler = (event) => {
    const msg = String(
      event?.message || event?.error?.message || event?.reason?.message || event?.reason || '',
    );
    if (!resizeObsNoise(msg)) return undefined;
    event.stopImmediatePropagation?.();
    event.preventDefault?.();
    return false;
  };
  window.addEventListener('error', handler, true);
  window.addEventListener('unhandledrejection', handler, true);
  if (process.env.NODE_ENV === 'development') {
    const origErr = console.error;
    console.error = (...args) => {
      const first = args[0];
      if (typeof first === 'string' && resizeObsNoise(first)) return;
      if (first instanceof Error && resizeObsNoise(first.message)) return;
      origErr.apply(console, args);
    };
  }
})();

/** Same artwork as public/favicon.ico; webpack emits under /static/media so Amplify/CloudFront always serves it (avoids root /favicon.ico 403/rewrite issues). */
function applyBundledFavicon() {
  ['icon', 'shortcut icon'].forEach((rel) => {
    let el = document.querySelector(`link[rel="${rel}"]`);
    if (!el) {
      el = document.createElement('link');
      el.rel = rel;
      document.head.appendChild(el);
    }
    el.setAttribute('type', 'image/x-icon');
    el.setAttribute('href', faviconUrl);
  });
}

applyBundledFavicon();

document.title = 'Smarthub is currently unavailable';

// Install the [LOCAL] / [STAGING] tab-title prefix observer. Runs after the
// initial document.title is set so that even the bootstrap title is tagged.
// Production is a no-op — getsmarthub.co tabs render unchanged.
installEnvTitlePrefix();

const root = createRoot(document.getElementById('root'));

root.render(
  <>
    {/* 1️⃣  Resets & normalises basic HTML elements */}
    <CssBaseline />

    {/* 2️⃣  Hard‑remove any default margin / ensure full‑height layout */}
    <GlobalStyles
      styles={{
        'html, body, #root': {
          height: '100%',
          margin: 0,
          padding: 0,
        },
        '*': { boxSizing: 'border-box' },
        '*::before, *::after': { boxSizing: 'inherit' },
      }}
    />

    <ShutdownApp />
  </>
);
